import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/complianceTour.css";

const TOUR_STORAGE_KEY = "ogiso_compliance_tour_completed";
const TOTAL_STEPS = 12;

let _tourActive = false;
export const isTourActive = () => _tourActive;

/* ─── Helpers ─── */

const waitForElement = (selector, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);
    const interval = setInterval(() => {
      const found = document.querySelector(selector);
      if (found) {
        clearInterval(interval);
        resolve(found);
      }
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
      reject(new Error(`Tour: ${selector} not found`));
    }, timeout);
  });
};

const scrollToCenter = (selector) => {
  const el = document.querySelector(selector);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

/* ─── Step Dots (progress indicator) ─── */

const dots = (current) => {
  let html = '<div class="tour-dots">';
  for (let i = 0; i < TOTAL_STEPS; i++) {
    html += `<span class="tour-dot${i === current ? " tour-dot-active" : ""}"></span>`;
  }
  html += "</div>";
  return html;
};

/* ─── Skip Link — added to every step ─── */

const skipLink = `<div class="tour-skip-row"><button class="tour-skip-link">Skip tour</button></div>`;

/* ─── Inline SVG Illustrations ─── */

const welcomeIllustration = `
<div class="tour-illustration">
  <svg width="100%" height="150" viewBox="0 0 360 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Card 1 — Shield (Document) -->
    <rect x="12" y="12" width="104" height="126" rx="12" fill="white" stroke="#FFE4A8" stroke-width="1.5"/>
    <circle cx="64" cy="52" r="24" fill="#FFF8EC"/>
    <path d="M64 34C64 34 52 40 52 50V58L64 64L76 58V50C76 40 64 34 64 34Z" fill="#FFBA45" opacity="0.2" stroke="#FFBA45" stroke-width="2" stroke-linejoin="round"/>
    <path d="M58 51L62 55L70 47" stroke="#FFBA45" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="28" y="86" width="72" height="8" rx="4" fill="#FFF2D4"/>
    <rect x="36" y="102" width="56" height="6" rx="3" fill="#FFF8EC"/>
    <text x="64" y="122" text-anchor="middle" fill="#D97706" font-size="10" font-weight="600" font-family="Inter,sans-serif">Document</text>

    <!-- Card 2 — Chart (Charting) -->
    <rect x="128" y="12" width="104" height="126" rx="12" fill="white" stroke="#BBF7D0" stroke-width="1.5"/>
    <circle cx="180" cy="52" r="24" fill="#F0FDF4"/>
    <rect x="166" y="44" width="4" height="18" rx="2" fill="#22C55E" opacity="0.4"/>
    <rect x="174" y="38" width="4" height="24" rx="2" fill="#22C55E" opacity="0.6"/>
    <rect x="182" y="48" width="4" height="14" rx="2" fill="#22C55E" opacity="0.4"/>
    <rect x="190" y="42" width="4" height="20" rx="2" fill="#22C55E"/>
    <rect x="144" y="86" width="72" height="8" rx="4" fill="#DCFCE7"/>
    <rect x="152" y="102" width="56" height="6" rx="3" fill="#F0FDF4"/>
    <text x="180" y="122" text-anchor="middle" fill="#16A34A" font-size="10" font-weight="600" font-family="Inter,sans-serif">Charting</text>

    <!-- Card 3 — Finance -->
    <rect x="244" y="12" width="104" height="126" rx="12" fill="white" stroke="#BFDBFE" stroke-width="1.5"/>
    <circle cx="296" cy="52" r="24" fill="#EFF6FF"/>
    <rect x="282" y="40" width="28" height="24" rx="4" fill="none" stroke="#3B82F6" stroke-width="2"/>
    <path d="M288 48L292 52L304 44" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="288" y1="58" x2="304" y2="58" stroke="#3B82F6" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
    <rect x="260" y="86" width="72" height="8" rx="4" fill="#DBEAFE"/>
    <rect x="268" y="102" width="56" height="6" rx="3" fill="#EFF6FF"/>
    <text x="296" y="122" text-anchor="middle" fill="#2563EB" font-size="10" font-weight="600" font-family="Inter,sans-serif">Finance</text>
  </svg>
</div>`;

const settingsIllustration = `
<div class="tour-illustration">
  <svg width="100%" height="130" viewBox="0 0 400 130" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Settings panel background -->
    <rect x="4" y="4" width="392" height="122" rx="12" fill="white" stroke="#E5E7EB" stroke-width="1"/>
    <!-- Tab bar -->
    <rect x="14" y="14" width="372" height="26" rx="6" fill="#F9FAFB"/>
    <rect x="18" y="18" width="70" height="18" rx="4" fill="#FFBA45" opacity="0.2"/>
    <text x="53" y="31" text-anchor="middle" fill="#D97706" font-size="8" font-weight="700" font-family="Inter,sans-serif">Documents</text>
    <rect x="96" y="18" width="54" height="18" rx="4" fill="transparent"/>
    <text x="123" y="31" text-anchor="middle" fill="#9CA3AF" font-size="8" font-weight="500" font-family="Inter,sans-serif">Charting</text>
    <rect x="158" y="18" width="42" height="18" rx="4" fill="transparent"/>
    <text x="179" y="31" text-anchor="middle" fill="#9CA3AF" font-size="8" font-weight="500" font-family="Inter,sans-serif">Alerts</text>
    <rect x="208" y="18" width="54" height="18" rx="4" fill="transparent"/>
    <text x="235" y="31" text-anchor="middle" fill="#9CA3AF" font-size="8" font-weight="500" font-family="Inter,sans-serif">Settings</text>
    <!-- Content rows -->
    <!-- Row 1 — active -->
    <rect x="14" y="50" width="372" height="20" rx="5" fill="#F0FDF4" stroke="#BBF7D0" stroke-width="0.8"/>
    <circle cx="30" cy="60" r="5" fill="#DCFCE7"/><path d="M28 60L29.5 61.5L33 58" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="42" y="57" width="100" height="5" rx="2.5" fill="#D1D5DB"/>
    <rect x="300" y="55" width="28" height="10" rx="10" fill="#22C55E"/>
    <circle cx="322" cy="60" r="4" fill="white"/>
    <text x="342" y="63" text-anchor="start" fill="#16A34A" font-size="7" font-weight="600" font-family="Inter,sans-serif">Active</text>
    <!-- Row 2 — active -->
    <rect x="14" y="76" width="372" height="20" rx="5" fill="#F0FDF4" stroke="#BBF7D0" stroke-width="0.8"/>
    <circle cx="30" cy="86" r="5" fill="#DCFCE7"/><path d="M28 86L29.5 87.5L33 84" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="42" y="83" width="80" height="5" rx="2.5" fill="#D1D5DB"/>
    <rect x="300" y="81" width="28" height="10" rx="10" fill="#22C55E"/>
    <circle cx="322" cy="86" r="4" fill="white"/>
    <text x="342" y="89" text-anchor="start" fill="#16A34A" font-size="7" font-weight="600" font-family="Inter,sans-serif">Active</text>
    <!-- Row 3 — inactive -->
    <rect x="14" y="102" width="372" height="20" rx="5" fill="#FEF2F2" stroke="#FECACA" stroke-width="0.8"/>
    <circle cx="30" cy="112" r="5" fill="#FEE2E2"/><line x1="27" y1="112" x2="33" y2="112" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="42" y="109" width="90" height="5" rx="2.5" fill="#D1D5DB"/>
    <rect x="300" y="107" width="28" height="10" rx="10" fill="#D1D5DB"/>
    <circle cx="306" cy="112" r="4" fill="white"/>
    <text x="342" y="115" text-anchor="start" fill="#9CA3AF" font-size="7" font-weight="600" font-family="Inter,sans-serif">Off</text>
  </svg>
</div>`;

const templatesIllustration = `
<div class="tour-illustration tour-illustration-sm">
  <svg width="100%" height="90" viewBox="0 0 320 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Folder 1 -->
    <rect x="10" y="18" width="90" height="60" rx="8" fill="white" stroke="#FFE4A8" stroke-width="1.2"/>
    <rect x="10" y="12" width="40" height="12" rx="4" fill="#FFF2D4" stroke="#FFE4A8" stroke-width="1"/>
    <rect x="22" y="34" width="56" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="22" y="44" width="42" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="22" y="54" width="48" height="5" rx="2.5" fill="#F3F4F6"/>
    <circle cx="80" cy="36" r="6" fill="#DCFCE7"/><path d="M77 36L79 38L83 34" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="80" cy="46" r="6" fill="#DCFCE7"/><path d="M77 46L79 48L83 44" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="80" cy="56" r="6" fill="#FEE2E2"/><line x1="77" y1="56" x2="83" y2="56" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Folder 2 -->
    <rect x="115" y="18" width="90" height="60" rx="8" fill="white" stroke="#BBF7D0" stroke-width="1.2"/>
    <rect x="115" y="12" width="40" height="12" rx="4" fill="#DCFCE7" stroke="#BBF7D0" stroke-width="1"/>
    <rect x="127" y="34" width="56" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="127" y="44" width="42" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="127" y="54" width="48" height="5" rx="2.5" fill="#F3F4F6"/>
    <circle cx="185" cy="36" r="6" fill="#DCFCE7"/><path d="M182 36L184 38L188 34" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="185" cy="46" r="6" fill="#DCFCE7"/><path d="M182 46L184 48L188 44" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="185" cy="56" r="6" fill="#DCFCE7"/><path d="M182 56L184 58L188 54" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Folder 3 -->
    <rect x="220" y="18" width="90" height="60" rx="8" fill="white" stroke="#BFDBFE" stroke-width="1.2"/>
    <rect x="220" y="12" width="40" height="12" rx="4" fill="#DBEAFE" stroke="#BFDBFE" stroke-width="1"/>
    <rect x="232" y="34" width="56" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="232" y="44" width="42" height="5" rx="2.5" fill="#F3F4F6"/>
    <rect x="232" y="54" width="48" height="5" rx="2.5" fill="#F3F4F6"/>
    <circle cx="290" cy="36" r="6" fill="#DCFCE7"/><path d="M287 36L289 38L293 34" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="290" cy="46" r="6" fill="#FEE2E2"/><line x1="287" y1="46" x2="293" y2="46" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="290" cy="56" r="6" fill="#DCFCE7"/><path d="M287 56L289 58L293 54" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Labels -->
    <text x="55" y="86" text-anchor="middle" fill="#D97706" font-size="9" font-weight="600" font-family="Inter,sans-serif">Staff</text>
    <text x="160" y="86" text-anchor="middle" fill="#16A34A" font-size="9" font-weight="600" font-family="Inter,sans-serif">Facility</text>
    <text x="265" y="86" text-anchor="middle" fill="#2563EB" font-size="9" font-weight="600" font-family="Inter,sans-serif">Client</text>
  </svg>
</div>`;

const chartingIllustration = `
<div class="tour-illustration tour-illustration-sm">
  <svg width="100%" height="80" viewBox="0 0 300 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Stat card 1 -->
    <rect x="4" y="4" width="88" height="72" rx="10" fill="white" stroke="#BBF7D0" stroke-width="1.2"/>
    <text x="48" y="30" text-anchor="middle" fill="#22C55E" font-size="22" font-weight="700" font-family="Inter,sans-serif">94%</text>
    <text x="48" y="46" text-anchor="middle" fill="#6B7280" font-size="9" font-weight="500" font-family="Inter,sans-serif">Notes</text>
    <rect x="20" y="56" width="56" height="6" rx="3" fill="#DCFCE7"/>
    <rect x="20" y="56" width="52" height="6" rx="3" fill="#22C55E" opacity="0.5"/>
    <!-- Stat card 2 -->
    <rect x="106" y="4" width="88" height="72" rx="10" fill="white" stroke="#BFDBFE" stroke-width="1.2"/>
    <text x="150" y="30" text-anchor="middle" fill="#3B82F6" font-size="22" font-weight="700" font-family="Inter,sans-serif">87%</text>
    <text x="150" y="46" text-anchor="middle" fill="#6B7280" font-size="9" font-weight="500" font-family="Inter,sans-serif">Tasks</text>
    <rect x="122" y="56" width="56" height="6" rx="3" fill="#DBEAFE"/>
    <rect x="122" y="56" width="48" height="6" rx="3" fill="#3B82F6" opacity="0.5"/>
    <!-- Stat card 3 -->
    <rect x="208" y="4" width="88" height="72" rx="10" fill="white" stroke="#FFE4A8" stroke-width="1.2"/>
    <text x="252" y="30" text-anchor="middle" fill="#D97706" font-size="22" font-weight="700" font-family="Inter,sans-serif">91%</text>
    <text x="252" y="46" text-anchor="middle" fill="#6B7280" font-size="9" font-weight="500" font-family="Inter,sans-serif">MAR</text>
    <rect x="224" y="56" width="56" height="6" rx="3" fill="#FFF2D4"/>
    <rect x="224" y="56" width="50" height="6" rx="3" fill="#FFBA45" opacity="0.5"/>
  </svg>
</div>`;

const operationalIllustration = `
<div class="tour-illustration tour-illustration-sm">
  <svg width="100%" height="80" viewBox="0 0 300 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Task row 1 — completed -->
    <rect x="4" y="4" width="292" height="22" rx="6" fill="white" stroke="#BBF7D0" stroke-width="1"/>
    <circle cx="18" cy="15" r="6" fill="#DCFCE7"/><path d="M15 15L17 17L21 13" stroke="#22C55E" stroke-width="1.3" stroke-linecap="round"/>
    <rect x="30" y="11" width="80" height="5" rx="2.5" fill="#E5E7EB"/>
    <rect x="200" y="10" width="40" height="8" rx="4" fill="#DCFCE7"/>
    <text x="220" y="17" text-anchor="middle" fill="#16A34A" font-size="7" font-weight="600" font-family="Inter,sans-serif">Done</text>
    <rect x="248" y="11" width="36" height="5" rx="2.5" fill="#F3F4F6"/>
    <!-- Task row 2 — overdue -->
    <rect x="4" y="30" width="292" height="22" rx="6" fill="white" stroke="#FEE2E2" stroke-width="1"/>
    <circle cx="18" cy="41" r="6" fill="#FEE2E2"/>
    <text x="18" y="44" text-anchor="middle" fill="#EF4444" font-size="8" font-weight="700" font-family="Inter,sans-serif">!</text>
    <rect x="30" y="37" width="90" height="5" rx="2.5" fill="#E5E7EB"/>
    <rect x="200" y="36" width="44" height="8" rx="4" fill="#FEE2E2"/>
    <text x="222" y="43" text-anchor="middle" fill="#DC2626" font-size="7" font-weight="600" font-family="Inter,sans-serif">Overdue</text>
    <rect x="252" y="37" width="36" height="5" rx="2.5" fill="#F3F4F6"/>
    <!-- Task row 3 — pending -->
    <rect x="4" y="56" width="292" height="22" rx="6" fill="white" stroke="#E5E7EB" stroke-width="1"/>
    <circle cx="18" cy="67" r="6" fill="#F3F4F6" stroke="#D1D5DB" stroke-width="1"/>
    <rect x="30" y="63" width="70" height="5" rx="2.5" fill="#E5E7EB"/>
    <rect x="200" y="62" width="44" height="8" rx="4" fill="#FFF2D4"/>
    <text x="222" y="69" text-anchor="middle" fill="#D97706" font-size="7" font-weight="600" font-family="Inter,sans-serif">Pending</text>
    <rect x="252" y="63" width="36" height="5" rx="2.5" fill="#F3F4F6"/>
  </svg>
</div>`;

const completionIllustration = `
<div class="tour-illustration tour-illustration-done">
  <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="44" fill="#FFF8EC" stroke="#FFBA45" stroke-width="3"/>
    <circle cx="50" cy="50" r="32" fill="#FFF2D4" opacity="0.5"/>
    <path d="M36 52L46 62L66 42" stroke="#FFBA45" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
</div>`;

/* ─── localStorage helpers ─── */

export const hasCompletedTour = () => {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const markTourComplete = () => {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  } catch {}
};

export const resetTour = () => {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  } catch {}
};

/* ─── Click-to-Advance Tracking ─── */

let _activeClickEl = null;
let _activeClickHandler = null;

const cleanupClick = () => {
  if (_activeClickEl && _activeClickHandler) {
    _activeClickEl.removeEventListener("click", _activeClickHandler);
    _activeClickEl.classList.remove("tour-clickable");
  }
  _activeClickEl = null;
  _activeClickHandler = null;
};

/**
 * Attach a one-time click listener on `selector`.
 * When clicked, the element de-pulses and the tour advances.
 */
const listenForClick = (driverObj, selector, beforeAdvance) => {
  cleanupClick();
  const el = document.querySelector(selector);
  if (!el) return;
  el.classList.add("tour-clickable");
  _activeClickEl = el;
  _activeClickHandler = () => {
    el.classList.remove("tour-clickable");
    if (beforeAdvance) beforeAdvance();
    setTimeout(() => driverObj.moveNext(), 450);
  };
  el.addEventListener("click", _activeClickHandler, { once: true });
};

/* ─── Tour Definition ─── */

/**
 * Start the compliance product tour.
 *
 * @param {Function} navigate  — React Router navigate()
 * @param {Function} setActiveTab — switches dashboard tabs (overview | charting | finance)
 */
export const startComplianceTour = (navigate, setActiveTab) => {
  if (_tourActive) return;
  _tourActive = true;
  /** Shared skip handler — wired to every .tour-skip-link */
  const skipTour = () => {
    cleanupClick();
    document.querySelectorAll(".tour-clickable").forEach((el) =>
      el.classList.remove("tour-clickable")
    );
    markTourComplete();
    _tourActive = false;
    driverObj.destroy();
    // Always land on dashboard overview when skipping
    if (setActiveTab) setActiveTab("overview");
    navigate("/home/dashboard");
  };

  const driverObj = driver({
    showProgress: false,
    animate: true,
    smoothScroll: true,
    allowClose: false,
    overlayColor: "rgba(0, 0, 0, 0.6)",
    stagePadding: 12,
    stageRadius: 12,
    popoverOffset: 16,
    disableActiveInteraction: false,

    /* Wire up skip links on EVERY step render */
    onPopoverRender: () => {
      document.querySelectorAll(".tour-skip-link").forEach((btn) => {
        btn.addEventListener("click", skipTour);
      });
    },

    onCloseClick: skipTour,

    onDestroyStarted: () => {
      cleanupClick();
      document.querySelectorAll(".tour-clickable").forEach((el) =>
        el.classList.remove("tour-clickable")
      );
      markTourComplete();
      _tourActive = false;
      driverObj.destroy();
    },

    steps: [
      /* ══════════════════════════════════════════
         1. Welcome — centered modal with illustration
         ══════════════════════════════════════════ */
      {
        popover: {
          title: "Welcome to Command Center",
          description: `
            ${welcomeIllustration}
            <p class="tour-body">Your centralized hub for tracking document compliance, charting rules, and business finances.</p>
            <p class="tour-sub">This quick tour will show you how to set up and monitor compliance across your organization.</p>
            ${dots(0)}
            ${skipLink}
          `,
          popoverClass: "tour-welcome",
          showButtons: ["next"],
          nextBtnText: "Start Tour",
        },
      },

      /* ══════════════════════════════════════════
         2. Navigate to Settings → Compliance tab
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="settings-compliance-tab"]',
        popover: {
          title: "Compliance Settings",
          description: `
            ${settingsIllustration}
            <p class="tour-body">This is your configuration hub. All document requirements, templates, and charting rules are managed here.</p>
            ${dots(1)}
            ${skipLink}
          `,
          side: "right",
          align: "start",
          popoverClass: "tour-info tour-info-wide",
          nextBtnText: "Next",
          prevBtnText: "Back",
        },
        onHighlightStarted: async () => {
          try {
            navigate("/home/settings?selectedTab=compliance");
            await waitForElement('[data-tour="settings-compliance-tab"]');
          } catch {}
        },
      },

      /* ══════════════════════════════════════════
         3. Document Templates (info)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="document-templates"]',
        popover: {
          title: "Document Templates",
          description: `
            ${templatesIllustration}
            <p class="tour-body">Configure required documents for staff, facilities, and clients. Active templates are automatically tracked in your compliance scores.</p>
            ${dots(2)}
            ${skipLink}
          `,
          side: "top",
          align: "start",
          popoverClass: "tour-info",
          nextBtnText: "Next",
          prevBtnText: "Back",
        },
      },

      /* ══════════════════════════════════════════
         4. Click folder to expand (click-required)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="template-folder"]',
        popover: {
          title: "Expand a Folder",
          description: `
            <p class="tour-body">Click on a document folder to see the templates inside. Each folder groups related compliance documents together.</p>
            <div class="tour-click-instruction">Click here to continue</div>
            ${dots(3)}
            ${skipLink}
          `,
          side: "bottom",
          align: "start",
          popoverClass: "tour-click-step",
          showButtons: [],
        },
        onHighlightStarted: async () => {
          try {
            await waitForElement('[data-tour="template-folder"]');
            scrollToCenter('[data-tour="template-folder"]');
            await new Promise((r) => setTimeout(r, 350));
          } catch {}
        },
        onHighlighted: () => {
          listenForClick(driverObj, '[data-tour="template-folder"]');
        },
        onDeselected: () => cleanupClick(),
      },

      /* ══════════════════════════════════════════
         5. Toggle a template switch (click-required)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="template-toggle"]',
        popover: {
          title: "Deactivate a Template",
          description: `
            <p class="tour-body">Use the toggle switch to activate or deactivate a document. Inactive templates won't appear in staff, facility, or client document folders.</p>
            <div class="tour-click-instruction">Toggle the switch to continue</div>
            ${dots(4)}
            ${skipLink}
          `,
          side: "bottom",
          align: "start",
          popoverClass: "tour-click-step",
          showButtons: [],
        },
        onHighlightStarted: async () => {
          try {
            await waitForElement('[data-tour="template-toggle"]');
            scrollToCenter('[data-tour="template-toggle"]');
            await new Promise((r) => setTimeout(r, 350));
          } catch {}
        },
        onHighlighted: () => {
          listenForClick(driverObj, '[data-tour="template-toggle"]');
        },
        onDeselected: () => cleanupClick(),
      },

      /* ══════════════════════════════════════════
         6. Click Charting sub-tab (click-required)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="settings-charting-tab"]',
        popover: {
          title: "Charting Rules",
          description: `
            <p class="tour-body">View and manage your charting compliance rules — notes, tasks, and MAR tracking per client.</p>
            <div class="tour-click-instruction">Click here to continue</div>
            ${dots(5)}
            ${skipLink}
          `,
          side: "bottom",
          align: "start",
          popoverClass: "tour-click-step",
          showButtons: [],
        },
        onHighlightStarted: async () => {
          scrollToCenter('[data-tour="settings-charting-tab"]');
          await new Promise((r) => setTimeout(r, 350));
        },
        onHighlighted: () => {
          listenForClick(driverObj, '[data-tour="settings-charting-tab"]');
        },
        onDeselected: () => cleanupClick(),
      },

      /* ══════════════════════════════════════════
         7. Navigate to Dashboard → Click Charting tab (click-required)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="charting-tab"]',
        popover: {
          title: "Charting Compliance",
          description: `
            <p class="tour-body">See how your charting rules translate into daily compliance scores across all clients and facilities.</p>
            <div class="tour-click-instruction">Click here to continue</div>
            ${dots(6)}
            ${skipLink}
          `,
          side: "bottom",
          align: "start",
          popoverClass: "tour-click-step",
          showButtons: [],
        },
        onHighlightStarted: async () => {
          try {
            navigate("/home/dashboard");
            await waitForElement('[data-tour="charting-tab"]');
            scrollToCenter('[data-tour="charting-tab"]');
            await new Promise((r) => setTimeout(r, 350));
          } catch {}
        },
        onHighlighted: () => {
          listenForClick(driverObj, '[data-tour="charting-tab"]', () => {
            if (setActiveTab) setActiveTab("charting");
          });
        },
        onDeselected: () => cleanupClick(),
      },

      /* ══════════════════════════════════════════
         8. Charting stat cards (info)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="charting-stats"]',
        popover: {
          title: "Charting Compliance Scores",
          description: `
            ${chartingIllustration}
            <p class="tour-body">These cards show daily compliance rates for <strong>Progress Notes</strong>, <strong>Tasks</strong>, and <strong>MAR</strong>. Each score reflects entries filed per client today.</p>
            ${dots(7)}
            ${skipLink}
          `,
          side: "bottom",
          align: "center",
          popoverClass: "tour-info",
          nextBtnText: "Next",
          prevBtnText: "Back",
        },
        onHighlightStarted: async () => {
          if (setActiveTab) setActiveTab("charting");
          try { await waitForElement('[data-tour="charting-stats"]'); } catch {}
        },
      },

      /* ══════════════════════════════════════════
         9. Additional Rules table (info)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="additional-rules"]',
        popover: {
          title: "Additional Charting Rules",
          description: `
            <p class="tour-body">Custom rules you create in Settings appear here with compliance rates, missing entries, and affected clients.</p>
            ${dots(8)}
            ${skipLink}
          `,
          side: "top",
          align: "start",
          popoverClass: "tour-info",
          nextBtnText: "Next",
          prevBtnText: "Back",
        },
      },

      /* ══════════════════════════════════════════
         10. Click Finance tab (click-required)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="finance-tab"]',
        popover: {
          title: "Financial Overview",
          description: `
            <p class="tour-body">Monitor your business revenue — monthly invoices, billing claims, and payment collections at a glance.</p>
            <div class="tour-click-instruction">Click here to continue</div>
            ${dots(9)}
            ${skipLink}
          `,
          side: "bottom",
          align: "start",
          popoverClass: "tour-click-step",
          showButtons: [],
        },
        onHighlightStarted: async () => {
          scrollToCenter('[data-tour="finance-tab"]');
          await new Promise((r) => setTimeout(r, 350));
        },
        onHighlighted: () => {
          listenForClick(driverObj, '[data-tour="finance-tab"]', () => {
            if (setActiveTab) setActiveTab("finance");
          });
        },
        onDeselected: () => cleanupClick(),
      },

      /* ══════════════════════════════════════════
         11. Finance Content (info)
         ══════════════════════════════════════════ */
      {
        element: '[data-tour="finance-content"]',
        popover: {
          title: "Revenue & Claims",
          description: `
            ${operationalIllustration}
            <p class="tour-body">Track monthly revenue from invoices and billing claims, monitor rejected claims, and view 6-month trends — all in one dashboard.</p>
            ${dots(10)}
            ${skipLink}
          `,
          side: "top",
          align: "center",
          popoverClass: "tour-info",
          nextBtnText: "Next",
          prevBtnText: "Back",
        },
        onHighlightStarted: async () => {
          if (setActiveTab) setActiveTab("finance");
          try { await waitForElement('[data-tour="finance-content"]'); } catch {}
        },
      },

      /* ══════════════════════════════════════════
         12. Completion — centered with illustration
         ══════════════════════════════════════════ */
      {
        popover: {
          title: "You're All Set!",
          description: `
            ${completionIllustration}
            <p class="tour-body">Your Command Center is ready. Start by configuring requirements in <strong>Settings &rarr; Compliance</strong>, then monitor everything from this dashboard.</p>
            <p class="tour-sub">You can retake this tour anytime from the header.</p>
            ${dots(11)}
          `,
          popoverClass: "tour-complete",
          showButtons: ["next"],
          doneBtnText: "Go to Dashboard",
        },
        onHighlightStarted: async () => {
          if (setActiveTab) setActiveTab("overview");
          await new Promise((r) => setTimeout(r, 300));
        },
      },
    ],
  });

  driverObj.drive();
};
